import React, { useEffect, useState } from "react";
import { confirm } from 'devextreme/ui/dialog';
import { useCountertops } from "../../contexts/countertopsContext";
import { format } from "date-fns";
import { useStoreInformation } from "../../api/get-store-information";
import { useEstimate } from "../../api/estimate";
import Popup from "devextreme-react/popup";
import ScrollView from "devextreme-react/scroll-view";
import { formatDate, formatNumber } from "devextreme/localization";
import Button from "devextreme-react/button";
import { useSaveEstimate } from "../../api/save-estimate";
import { useNavigate } from "react-router";
import { ValidationSummary } from "devextreme-react/validation-summary";
import LoadPanel from "devextreme-react/load-panel";
import { Item, Toolbar } from "devextreme-react/toolbar";
import { showErrorMessage } from "../../../../../utils/messages";
import { queryClient } from "../../../../../lib/react-query";
import { formatMoney } from "../../../../../utils/string-utils";

export function TotalControl() {
    const context = useCountertops();
    const storeInformation = useStoreInformation(context.dealerId);
    const [svEstimateInfo, setSvEstimateInfo] = useState<{
        isSuccess: boolean,
        estimateNumber: string,
        paymentUrl: string
    }>();
    const [isPopupVisible, setPopupVisibility] = useState(false);
    const [isSaveAndSubmitBtn, setSaveAndSubmitBtn] = useState(false);
    const [isEstimatePopupVisible, setIsEstimatePopupVisible] = useState(false);
    const navigate = useNavigate();
    const pricing = useEstimate(context.dealerId, context.getProductsToEstimate(), context.customerZip);

    const svEstimate = useSaveEstimate(
        getSvEstimateRequest(),
        (result) => {
            if (result.isSuccess) {
                setSvEstimateInfo({
                    isSuccess: result.isSuccess!,
                    estimateNumber: result.estimateNumber!,
                    paymentUrl: result.paymentUrl!,
                });
            }
        },
        (error) => {
            showErrorMessage(error);
        });

    useEffect(() => {
        pricing.refetch();
    }, [pricing, context.shapes])

    useEffect(() => {
        // Capture retail price bucket ID from pricing response
        if (pricing.data?.retailPriceBucketId && pricing.data.retailPriceBucketId !== context.retailPriceBucketID) {
            context.setRetailPriceBucketID(pricing.data.retailPriceBucketId);
        }
    }, [pricing.data])

    useEffect(() => {
        if (svEstimateInfo?.isSuccess) {
            setIsEstimatePopupVisible(true);
        }
    }, [svEstimateInfo?.isSuccess])

    const togglePopup = () => {
        setPopupVisibility(!isPopupVisible);
    };

    const toggleEstimatePopup = () => {
        setIsEstimatePopupVisible(!isEstimatePopupVisible);
        navigate('/home')
    };

    function getSvEstimateRequest() {
        let req = context.getSaveEstimateRequest();
        req.saveAction = isSaveAndSubmitBtn ? 'Send' : '';
        return req;
    }

    function SaveEstimate(btnSaveAndSubmit: boolean) {
        setSaveAndSubmitBtn(btnSaveAndSubmit);
        let request = context.getSaveEstimateRequest();
        svEstimate.mutate();

        queryClient.invalidateQueries({ queryKey: ['ee-pro-get-estimate', (request.quoteId ?? 0).toString()] });
    }

    const renderContent = () => {
        return (
            <div className={"ee-pro-total-modal"}>
                <ScrollView width={"100%"} height={"100%"}>
                    <table style={{ "width": "100%" }}>
                        <tbody>
                            {pricing.data.products.map(x => <tr key={x.name + '_' + x.customDescription ?? '' + x.qty}>
                                <td>{x.name}{x.customDescription !== null ? <p>{x.customDescription}</p> : <></>}</td>
                                <td style={{
                                    textAlign: "right",
                                    width: "120px"
                                }}>{formatNumber(x.qty, "decimal")}</td>
                                <td style={{ textAlign: "right", width: "120px" }}>{formatMoney(x.price)}</td>
                            </tr>)}
                            <tr>
                                <td style={{ textAlign: "right", width: "120px" }} className="ee-pro-single-line"
                                    colSpan={2}>SubTotal:
                                </td>
                                <td style={{
                                    textAlign: "right",
                                    width: "120px"
                                }} className="ee-pro-single-line">{formatMoney(pricing.data.subTotal)}</td>
                            </tr>
                            {
                                pricing.data.discount > 0 ? <tr>
                                    <td style={{ textAlign: "right", width: "120px" }}
                                        colSpan={2}>Savings:
                                    </td>
                                    <td style={{
                                        textAlign: "right",
                                        width: "120px"
                                    }}>{formatMoney(pricing.data.discount)}</td>
                                </tr>
                                    : <></>
                            }
                            <tr>
                                <td style={{ textAlign: "right", width: "120px", fontSize: "14px" }}
                                    className="ee-pro-double-line"
                                    colSpan={2}>Total:
                                </td>
                                <td style={{
                                    textAlign: "right",
                                    width: "120px",
                                }} className="ee-pro-double-line">{formatMoney(pricing.data.total)}</td>
                            </tr>
                            {
                                pricing.data.promoEnd !== null ? <tr>
                                    <td>
                                    </td>
                                    <td style={{
                                        textAlign: "right",
                                        color: "red",
                                        fontSize: "10px"
                                    }} colSpan={2}>Promotion ends on {formatDate(pricing.data.promoEnd, "shortDate")}</td>
                                </tr>
                                    : <></>
                            }
                        </tbody>
                    </table>
                </ScrollView>
            </div>
        );
    }

    const renderEstimateContent = () => {
        return (
            <div className={"ee-pro-estimate-submit-modal"} style={{ textAlign: "center" }}>
                <p>Estimate {svEstimateInfo?.estimateNumber} has been submitted successfully</p>
                {svEstimateInfo?.paymentUrl ? <button onClick={() => {
                    window.open(svEstimateInfo?.paymentUrl);
                }}>Continue to Payment</button> : null}
                <div style={{ marginTop: "10px" }}>
                    <button onClick={() => toggleEstimatePopup()}>Return to Portal</button>
                </div>
            </div>
        );
    }

    return (
        <>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                position={{ of: '#EEPro' }}
                visible={svEstimate.isLoading || context.isLoading}
            />
            <p className={"box-header"}>
                Total Cost
                <a onClick={() => {
                    setPopupVisibility(true);
                }}>
                    <i className={"dx-icon-info estimate-info"}></i>
                </a>
            </p>
            <Popup
                title={"Order Summary A"}
                visible={isPopupVisible}
                hideOnOutsideClick={true}
                width={500}
                onHiding={togglePopup}
                showCloseButton={true}
                contentRender={renderContent}
            >
            </Popup>
            <Popup
                title={"Estimate"}
                visible={isEstimatePopupVisible}
                hideOnOutsideClick={true}
                width={400}
                height={svEstimateInfo?.paymentUrl ? 240 : 200}
                onHiding={toggleEstimatePopup}
                showCloseButton={true}
                contentRender={renderEstimateContent}
            >
            </Popup>
            {svEstimateInfo?.isSuccess ? <div /> : null}
            <div style={{ width: "200px", float: "left" }}>
                <p className={"title-small"}>
                    Estimate Information
                </p>
                <table>
                    <tbody>
                        <tr>
                            <td>Quote Date:</td>
                            <td>{format(context.estimateDate, "MM/dd/yyyy")}</td>
                        </tr>
                        <tr>
                            <td>Expires On:</td>
                            <td>{format(context.expirationDate, "MM/dd/yyyy")}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {!storeInformation.isLoading &&
                <div style={{ width: "240px", float: "left", marginLeft: "40px" }}>
                    <p className={"title-small"}>
                        Store Information
                    </p>
                    <table>
                        <tbody>
                            <tr>
                                <td>{storeInformation.data.name}</td>
                            </tr>
                            <tr>
                                <td>{storeInformation.data.address1}</td>
                            </tr>
                            {storeInformation.data.address2 &&
                                <tr>
                                    <td>{storeInformation.data.address2}</td>
                                </tr>
                            }
                            <tr>
                                <td>{storeInformation.data.cityStateZip}</td>
                            </tr>
                            <tr>
                                <td>{storeInformation.data.phone}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            }
            <div style={{ width: "200px", float: "right" }}>
                <table style={{ borderCollapse: "collapse" }}>
                    <tbody>
                        <tr>
                            <td style={{ textAlign: "right", width: "120px" }}
                                colSpan={2}>SubTotal:
                            </td>
                            <td style={{
                                textAlign: "right",
                                width: "120px"
                            }}>{formatMoney(pricing.data.subTotal)}</td>
                        </tr>
                        {
                            pricing.data.discount > 0 ? <tr>
                                <td style={{ textAlign: "right", width: "120px" }}
                                    colSpan={2}>Savings:
                                </td>
                                <td style={{
                                    textAlign: "right",
                                    width: "120px"
                                }}>{formatMoney(pricing.data.discount)}</td>
                            </tr>
                                : <></>
                        }
                        <tr>
                            <td style={{ textAlign: "right", width: "120px", fontSize: "14px" }}
                                className="ee-pro-double-line"
                                colSpan={2}>Total:
                            </td>
                            <td style={{
                                textAlign: "right",
                                width: "120px",
                            }} className="ee-pro-double-line">{formatMoney(pricing.data.total)}</td>
                        </tr>
                        {
                            pricing.data.promoEnd !== null ? <tr>
                                <td>
                                </td>
                                <td style={{
                                    textAlign: "right",
                                    color: "red",
                                    fontSize: "10px"
                                }} colSpan={2}>Promotion ends on {formatDate(pricing.data.promoEnd, "shortDate")}</td>
                            </tr>
                                : <></>
                        }
                    </tbody>
                </table>
            </div>
            <div style={{ clear: "both" }}></div>
            <div style={{ float: "right" }}>
                <ValidationSummary></ValidationSummary>
            </div>
            <div style={{ clear: "both" }}></div>

            <Toolbar className="button-toolbar">
                <Item location={'after'}>
                </Item>
                <Item location="after">
                    <Button
                        text="Save Estimate"
                        icon="save"
                        disabled={context.shapes.length === 0 || !context.shapes.some(x => x.productID !== null)}
                        onClick={(params) => {
                            let validationResult = params.validationGroup.validate();
                            if (validationResult.isValid) {
                                let result = confirm("Are you sure you want to save the estimate?", "");
                                result.then((dialogResult) => {
                                    if (!dialogResult)
                                        return;

                                    SaveEstimate(false);
                                });
                            }
                        }}
                    />
                </Item>
            </Toolbar>
        </>
    );
}
